// Stateful coordinator for the four-file episode upload flow.
//
// Owns one upload-progress record per slot. Triggering `start(slot, file)`
// orchestrates the three-step flow:
//   1. POST /upload-:slot   (presigned URL)
//   2. PUT to R2 (XHR with progress events)
//   3. POST /upload-:slot/complete
//
// On any network error the slot returns to `idle` with `error` set so the
// user can retry without rebuilding wizard state.

import { useCallback, useMemo, useState } from 'react'
import {
  type EpisodeFileSlot,
  uploadToPresigned,
  useCompleteEpisodeUpload,
  useRequestEpisodeUpload,
} from '../../lib/api/episodes'

export interface SlotState {
  status: 'idle' | 'requesting' | 'uploading' | 'completing' | 'done' | 'error'
  file: { name: string; size: number } | null
  /** 0..1, only meaningful while status === 'uploading'. */
  progress: number
  error: string | null
  objectKey: string | null
}

const blankState: SlotState = {
  status: 'idle',
  file: null,
  progress: 0,
  error: null,
  objectKey: null,
}

export function useEpisodeUploads(episodeId: string | null) {
  const [video, setVideo] = useState<SlotState>(blankState)
  const [refAudio, setRefAudio] = useState<SlotState>(blankState)
  const [script, setScript] = useState<SlotState>(blankState)
  const [srt, setSrt] = useState<SlotState>(blankState)

  const setters: Record<EpisodeFileSlot, React.Dispatch<React.SetStateAction<SlotState>>> = useMemo(
    () => ({ video: setVideo, ref_audio: setRefAudio, script: setScript, srt: setSrt }),
    [],
  )

  const states: Record<EpisodeFileSlot, SlotState> = { video, ref_audio: refAudio, script, srt }

  const requestUpload = useRequestEpisodeUpload()
  const completeUpload = useCompleteEpisodeUpload()

  const start = useCallback(
    async (slot: EpisodeFileSlot, file: File): Promise<void> => {
      if (!episodeId) {
        setters[slot]({
          ...blankState,
          file: { name: file.name, size: file.size },
          status: 'error',
          error: 'No episode created yet — go back to Step 1.',
        })
        return
      }
      setters[slot]({
        ...blankState,
        file: { name: file.name, size: file.size },
        status: 'requesting',
      })
      try {
        const presigned = await requestUpload.mutateAsync({
          episodeId,
          slot,
          filename: file.name,
          byteSize: file.size,
        })
        setters[slot]((s) => ({ ...s, status: 'uploading', objectKey: presigned.object_key }))
        await uploadToPresigned(presigned, file, undefined, (loaded, total) => {
          setters[slot]((s) => ({ ...s, progress: total > 0 ? loaded / total : 0 }))
        })
        setters[slot]((s) => ({ ...s, status: 'completing', progress: 1 }))
        await completeUpload.mutateAsync({
          episodeId,
          slot,
          objectKey: presigned.object_key,
        })
        setters[slot]((s) => ({ ...s, status: 'done' }))
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Upload failed'
        setters[slot]((s) => ({ ...s, status: 'error', error: message }))
      }
    },
    [episodeId, requestUpload, completeUpload, setters],
  )

  /** True iff the two required slots are uploaded. */
  const requiredDone = states.video.status === 'done' && states.ref_audio.status === 'done'
  /** Total uploaded (in 0..4 sense), used for the "next" gate UX hint. */
  const optionalDoneCount =
    (states.script.status === 'done' ? 1 : 0) + (states.srt.status === 'done' ? 1 : 0)

  return { states, start, requiredDone, optionalDoneCount }
}
